import { type FC, useMemo, useState } from "react";
import { Checkbox, InputNumber, Select } from "antd";
import {
  DownOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import TableHeaderCell from "../shared/TableHeaderCell";
import { usePlotOutline } from "../../hooks/usePlotOutline";
import "./style.less";

export interface GroupWiseTableRow {
  id: string;
  plot: string;
  productLabel: string;
  productOptions?: string[];
  seedRate?: number;
  checked?: boolean;
  // Groups the accent bar by identity (e.g. treatment) instead of row
  // position: rows sharing the same key get the same colour, `null` renders
  // the neutral/untreated colour, and leaving it out keeps the legacy
  // per-group colour used by the seed-rate table.
  accentColorKey?: string | null;
  accentColor?: string;
}

export interface GroupWiseTableGroup {
  id: string;
  label: string;
  rows: GroupWiseTableRow[];
  defaultExpanded?: boolean;
}

export interface GroupWiseTableColumnConfig {
  plotColumnLabel?: string;
  plotColumnUnit?: string;
  selectColumnLabel?: string;
  selectColumnUnit?: string;
  seedRateColumnLabel?: string;
  seedRateColumnUnit?: string;
  showCheckbox?: boolean;
  showSeedRate?: boolean;
  showSelectIcon?: boolean;
}

const buildAccentColorMap = (
  groups: GroupWiseTableGroup[]
): Map<string, number> => {
  const map = new Map<string, number>();
  groups.forEach((group) => {
    group.rows.forEach((row) => {
      if (row.accentColorKey != null && !map.has(row.accentColorKey)) {
        map.set(row.accentColorKey, map.size);
      }
    });
  });
  return map;
};

const getAccentClassName = (
  row: GroupWiseTableRow,
  fallbackIndex: number,
  accentColorMap: Map<string, number>
): string => {
  if (row.accentColorKey === null) {
    return "";
  }
  const colorIndex =
    row.accentColorKey != null
      ? (accentColorMap.get(row.accentColorKey) as number)
      : fallbackIndex;
  return `rd-groupwise-table__accent--${(colorIndex % 4) + 1}`;
};

export interface GroupWiseTableProps extends GroupWiseTableColumnConfig {
  groups: GroupWiseTableGroup[];
  onToggleRow?: (rowId: string, checked: boolean) => void;
  onShowPlot?: (rowId: string) => void;
  onProductChange?: (rowId: string, value: string) => void;
  onSeedRateChange?: (rowId: string, value: number | null) => void;
  className?: string;
}

interface GroupSectionProps extends GroupWiseTableColumnConfig {
  group: GroupWiseTableGroup;
  accentIndex: number;
  accentColorMap: Map<string, number>;
  onToggleRow?: (rowId: string, checked: boolean) => void;
  onShowPlot?: (rowId: string) => void;
  onProductChange?: (rowId: string, value: string) => void;
  onSeedRateChange?: (rowId: string, value: number | null) => void;
}

const GroupSection: FC<GroupSectionProps> = ({
  group,
  accentIndex,
  accentColorMap,
  onToggleRow,
  onShowPlot,
  onProductChange,
  onSeedRateChange,
  plotColumnLabel,
  plotColumnUnit,
  selectColumnLabel,
  selectColumnUnit,
  seedRateColumnLabel,
  seedRateColumnUnit,
  showCheckbox = true,
  showSeedRate = true,
  showSelectIcon = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(group.defaultExpanded ?? true);
  const { isOutlined, toggleOutline } = usePlotOutline();

  return (
    <div className="rd-groupwise-table__group">
      <button
        type="button"
        className="rd-groupwise-table__collapse-header"
        aria-expanded={expanded}
        onClick={() => {
          setExpanded((prev) => !prev);
        }}
      >
        {expanded ? (
          <DownOutlined
            className="rd-groupwise-table__collapse-icon"
            aria-hidden="true"
          />
        ) : (
          <RightOutlined
            className="rd-groupwise-table__collapse-icon"
            aria-hidden="true"
          />
        )}
        <span className="rd-groupwise-table__collapse-label">
          {group.label}
        </span>
      </button>
      {expanded && (
        <div className="rd-groupwise-table__grid" role="table">
          <div className="rd-groupwise-table__header" role="row">
            <TableHeaderCell
              classPrefix="rd-groupwise-table"
              label={
                plotColumnLabel ??
                formatTranslation(t("newRatesAndDosage.groupWiseTable.plots"))
              }
              unit={
                plotColumnUnit ??
                formatTranslation(
                  t("newRatesAndDosage.groupWiseTable.plotsUnit")
                )
              }
              columnClassName="rd-groupwise-table__col--plots"
              showIcons={false}
            />
            <TableHeaderCell
              classPrefix="rd-groupwise-table"
              label={
                selectColumnLabel ??
                formatTranslation(t("newRatesAndDosage.groupWiseTable.product"))
              }
              unit={
                selectColumnUnit ??
                formatTranslation(
                  t("newRatesAndDosage.groupWiseTable.productUnit")
                )
              }
              columnClassName={`rd-groupwise-table__col--product ${
                showSeedRate ? "" : "rd-groupwise-table__col--product--wide"
              }`}
              showIcons={false}
            />
            {showSeedRate && (
              <TableHeaderCell
                classPrefix="rd-groupwise-table"
                label={
                  seedRateColumnLabel ??
                  formatTranslation(
                    t("newRatesAndDosage.groupWiseTable.seedRate")
                  )
                }
                unit={
                  seedRateColumnUnit ??
                  formatTranslation(
                    t("newRatesAndDosage.groupWiseTable.seedRateUnit")
                  )
                }
                columnClassName="rd-groupwise-table__col--seed-rate"
                showIcons={false}
              />
            )}
            {/* Keeps the header's flexible column pixel-aligned with the
                body rows, which always end with a fixed-width show cell. */}
            <div
              className="rd-groupwise-table__header-cell rd-groupwise-table__col--show"
              aria-hidden="true"
            />
          </div>
          <div className="rd-groupwise-table__body">
            {group.rows.map((row) => {
              const isPlotVisible = isOutlined(row.id);
              const handleToggleVisibility = (): void => {
                toggleOutline(row.id);
                onShowPlot?.(row.id);
              };
              const showButton = (
                <button
                  type="button"
                  className={`rd-groupwise-table__show-btn ${
                    isPlotVisible
                      ? "rd-groupwise-table__show-btn--visible"
                      : "rd-groupwise-table__show-btn--invisible"
                  }`}
                  aria-pressed={isPlotVisible}
                  aria-label={formatTranslation(
                    t("newRatesAndDosage.groupWiseTable.showPlot", {
                      plot: row.plot,
                    })
                  )}
                  onClick={handleToggleVisibility}
                >
                  {isPlotVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                </button>
              );

              return (
                <div
                  key={row.id}
                  className="rd-groupwise-table__row"
                  role="row"
                  data-testid="groupwise-table-row"
                >
                  <div
                    className="rd-groupwise-table__cell rd-groupwise-table__col--plots"
                    role="cell"
                  >
                    <span
                      className={`rd-groupwise-table__accent ${getAccentClassName(
                        row,
                        accentIndex,
                        accentColorMap
                      )}`}
                      style={
                        row.accentColor
                          ? { backgroundColor: row.accentColor }
                          : undefined
                      }
                      aria-hidden="true"
                    />
                    {showCheckbox && (
                      <Checkbox
                        checked={row.checked}
                        onChange={(e) =>
                          onToggleRow?.(row.id, e.target.checked)
                        }
                        aria-label={formatTranslation(
                          t("newRatesAndDosage.groupWiseTable.selectRow", {
                            plot: row.plot,
                          })
                        )}
                      />
                    )}
                    <span className="rd-groupwise-table__plot-label">
                      {row.plot}
                    </span>
                  </div>
                  <div
                    className={`rd-groupwise-table__cell rd-groupwise-table__col--product ${
                      showSeedRate
                        ? ""
                        : "rd-groupwise-table__col--product--wide"
                    }`}
                    role="cell"
                  >
                    {showSelectIcon && (
                      <span
                        className="rd-groupwise-table__select-icon"
                        aria-hidden="true"
                      >
                        <SearchOutlined />
                      </span>
                    )}
                    <Select
                      className="rd-groupwise-table__product-select"
                      value={row.productLabel}
                      options={(row.productOptions ?? [row.productLabel]).map(
                        (option) => ({ label: option, value: option })
                      )}
                      onChange={(value) => onProductChange?.(row.id, value)}
                      suffixIcon={<DownOutlined />}
                      aria-label={formatTranslation(
                        t("newRatesAndDosage.groupWiseTable.productSelect", {
                          plot: row.plot,
                        })
                      )}
                    />
                  </div>
                  {showSeedRate && (
                    <div
                      className="rd-groupwise-table__cell rd-groupwise-table__col--seed-rate"
                      role="cell"
                    >
                      <InputNumber
                        className="rd-groupwise-table__rate-input"
                        value={row.seedRate}
                        controls
                        onChange={(value) => onSeedRateChange?.(row.id, value)}
                        aria-label={formatTranslation(
                          t("newRatesAndDosage.groupWiseTable.seedRateInput", {
                            plot: row.plot,
                          })
                        )}
                      />
                    </div>
                  )}
                  <div
                    className="rd-groupwise-table__cell rd-groupwise-table__col--show"
                    role="cell"
                  >
                    {showButton}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const GroupWiseTable: FC<GroupWiseTableProps> = ({
  groups,
  onToggleRow,
  onShowPlot,
  onProductChange,
  onSeedRateChange,
  plotColumnLabel,
  plotColumnUnit,
  selectColumnLabel,
  selectColumnUnit,
  seedRateColumnLabel,
  seedRateColumnUnit,
  showCheckbox,
  showSeedRate,
  showSelectIcon,
  className,
}) => {
  const accentColorMap = useMemo(() => buildAccentColorMap(groups), [groups]);

  return (
    <div
      className={`rd-groupwise-table ${className ?? ""}`}
      data-testid="groupwise-table"
    >
      {groups.map((group, index) => (
        <GroupSection
          key={group.id}
          group={group}
          accentIndex={index}
          accentColorMap={accentColorMap}
          onToggleRow={onToggleRow}
          onShowPlot={onShowPlot}
          onProductChange={onProductChange}
          onSeedRateChange={onSeedRateChange}
          plotColumnLabel={plotColumnLabel}
          plotColumnUnit={plotColumnUnit}
          selectColumnLabel={selectColumnLabel}
          selectColumnUnit={selectColumnUnit}
          seedRateColumnLabel={seedRateColumnLabel}
          seedRateColumnUnit={seedRateColumnUnit}
          showCheckbox={showCheckbox}
          showSeedRate={showSeedRate}
          showSelectIcon={showSelectIcon}
        />
      ))}
    </div>
  );
};

export default GroupWiseTable;
